export const POST_PRODUCTION_GENERATION_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  READY: 'ready',
  FAILED: 'failed',
} as const;

export type PostProductionGenerationStatus =
  (typeof POST_PRODUCTION_GENERATION_STATUS)[keyof typeof POST_PRODUCTION_GENERATION_STATUS];

export const IN_FLIGHT_GENERATION_STATUSES: readonly PostProductionGenerationStatus[] =
  [
    POST_PRODUCTION_GENERATION_STATUS.PENDING,
    POST_PRODUCTION_GENERATION_STATUS.PROCESSING,
  ];

export const POST_PRODUCTION_GENERATION_TYPE = {
  RESIZE: 'resize',
} as const;

export type PostProductionGenerationKind =
  (typeof POST_PRODUCTION_GENERATION_TYPE)[keyof typeof POST_PRODUCTION_GENERATION_TYPE];

export const TARGET_RESOLUTIONS = [
  '1080x1080',
  '1080x1350',
  '1350x1080',
  '1080x1920',
  '1920x1080',
] as const;

export type TargetResolution = (typeof TARGET_RESOLUTIONS)[number];

export type ResolutionOption = {
  resolution: TargetResolution;
  label: string;
  orientation: 'square' | 'portrait' | 'landscape';
};

export const RESOLUTION_OPTIONS: readonly ResolutionOption[] = [
  {resolution: '1080x1080', label: 'Square (1:1)', orientation: 'square'},
  {resolution: '1080x1350', label: 'Portrait (4:5)', orientation: 'portrait'},
  {resolution: '1080x1920', label: 'Vertical (9:16)', orientation: 'portrait'},
  {resolution: '1350x1080', label: 'Landscape (5:4)', orientation: 'landscape'},
  {
    resolution: '1920x1080',
    label: 'Widescreen (16:9)',
    orientation: 'landscape',
  },
];

const ASPECT_RATIO_TOLERANCE = 0.02;

export function resolutionAspectRatio(resolution: TargetResolution): number {
  const [width, height] = resolution.split('x').map(Number);
  return width / height;
}

export function matchesAspectRatio(
  resolution: TargetResolution,
  width: number,
  height: number
): boolean {
  if (!width || !height) return false;
  return (
    Math.abs(resolutionAspectRatio(resolution) - width / height) <
    ASPECT_RATIO_TOLERANCE
  );
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

export function formatAspectRatio(width: number, height: number): string {
  const divisor = greatestCommonDivisor(width, height) || 1;
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
}

export type PostProductionGeneration = {
  id: string;
  generationType: PostProductionGenerationKind;
  status: PostProductionGenerationStatus;
  targetResolution: TargetResolution;
  url: string | null;
  thumbnailUrl: string | null;
  failureReason: string | null;
};

export const MAX_SOURCE_DURATION_SECONDS = 60;
export const MAX_SOURCE_DIMENSION_PX = 1920;

export type PostProduction = {
  generations: PostProductionGeneration[];
  /** Slots left per type (server-computed). */
  remaining: Record<PostProductionGenerationKind, number>;
  /** Effective per-type cap (server-computed; reflects Swaps overrides). */
  limits: Record<PostProductionGenerationKind, number>;
};

export const RESIZE_PLAYGROUND_URL = 'https://console.ltx.video/playground/';

export const CONTACT_SALES_EMAIL = 'sales@popularpays.com';
export const CONTACT_SALES_SUBJECT = 'Request more video resizes';
export const RESIZE_CONTACT_SALES_URL = `mailto:${CONTACT_SALES_EMAIL}?subject=${encodeURIComponent(
  CONTACT_SALES_SUBJECT
)}`;
